import Button from '@pelckmans/elementis/components/button';
import Heading from '@pelckmans/elementis/components/heading';
import SelectField from '@pelckmans/elementis/components/select-field';
import TextField from '@pelckmans/elementis/components/text-field';
import { useToast } from '@pelckmans/elementis/components/toast';
import { Fragment } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group } from '../../../../../../api/groups';
import { GroupModuleYearUnwind } from '../../../../../../api/my-groups';
import { TOAST_ID } from '../../../../../../constants/ToastIds';
import { SchoolYear } from '../../../../hooks/useGroups';
import { FormValues } from '../EditGroupModal.vo';

interface Props {
  group: Group;
  linkedModules: GroupModuleYearUnwind[];
  selectedSchoolyear: SchoolYear;
}

export default function EditGroupForm({ group, linkedModules, selectedSchoolyear }: Props) {
  const { t } = useTranslation();
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FormValues>();
  const { addToast } = useToast();

  const status = useWatch({ control, name: 'status' });
  const isPublished = status === 'published';
  const hideControls = status === 'closed' && selectedSchoolyear.hasPassed;

  const handleCopy = () => {
    if (group.code) {
      navigator.clipboard.writeText(group.code);
      addToast({
        id: TOAST_ID.COPIED_COURSE_CODE,
        variant: 'success',
        title: t(`PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.CODE.TOAST.TITLE`),
        children: t(`PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.CODE.TOAST.MESSAGE`),
      });
    }
  };

  return (
    <div className="edit-vo-group-form">
      <Heading level={4} removeSpacing="all">
        {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.TITLE')}
      </Heading>

      <TextField
        id="name"
        key="name"
        labelText={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.COURSE_NAME.LABEL')}
        autoComplete="on"
        required
        placeholder={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.COURSE_NAME.PLACEHOLDER')}
        error={!!errors.name}
        errorText={errors.name?.message}
        {...register('name', {
          required: t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.COURSE_NAME.ERROR_REQUIRED'),
          maxLength: { value: 25, message: t('FORM.VALIDATION.MAX_LENGTH', { max: 25 }) },
        })}
      />

      <SelectField
        labelText={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.SCHOOL.LABEL')}
        required
        renderValueContent={school => (school ? school.name : '')}
        dropdownMenuProps={{
          ariaLabel: undefined,
        }}
        value={group.school}
        onChange={() => undefined}
        disabled
      >
        <SelectField.Item key={group.school.id} text={group.school.name} value={group.school} />
      </SelectField>

      {!hideControls && (
        <>
          <Controller
            control={control}
            shouldUnregister={false}
            name="linkedModulesCount"
            defaultValue={linkedModules.length}
            rules={{
              validate: value =>
                !isPublished || value > 0
                  ? true
                  : t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.ALERTS.NO_MODULES_SELECTED'),
            }}
            render={() => <Fragment />}
          />
          <Controller
            control={control}
            shouldUnregister={false}
            name="totalAccepted"
            rules={{
              validate: value =>
                !isPublished || value > 0
                  ? true
                  : t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.AVAILABILITY.ALERTS.NO_STUDENTS_REGISTERED'),
            }}
            render={() => <Fragment />}
          />
        </>
      )}

      {group.status !== 'closed' && (
        <TextField
          id="code"
          key="code"
          labelText={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.CODE.LABEL')}
          required
          placeholder={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.CODE.PLACEHOLDER')}
          value={group.status === 'published' ? group.code : ''}
          disabled
          readOnly
          buttonGroupProps={
            group.status === 'published'
              ? {
                  'aria-label': undefined,
                  role: undefined,
                  size: 'sm',
                  children: (
                    <Button prominence="neutral" key="copy-code" onClick={handleCopy}>
                      {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.CODE.COPY_ACTION.LABEL')}
                    </Button>
                  ),
                }
              : undefined
          }
          helpText={
            !isPublished ? (
              <span dangerouslySetInnerHTML={{ __html: t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.FORM.CODE.HELP_TEXT') }} />
            ) : undefined
          }
        />
      )}
    </div>
  );
}
